import axios from 'axios'
import { cloneDeep, range, has } from 'lodash'
import BaseModel from './base'
import CalendarService from '../../services/CalendarService'

export interface ICalendar {
  locationId: string
  groupId?: string
  teamMembers?: ITeamMember[]
  eventType?: EventType
  addServiceActiveStep?: ActiveStepType
  name: string
  description?: string
  slug?: string
  eventTitle?: string
  eventColor?: string
  meetingLocation?: string
  linkedCalendars?: ILinkedCalendars
  syncOption?: SyncOption
  reserveService?: boolean
  homeServiceData?: IHomeServiceData
  price?: IHomeServicePrice
  slotDuration: number
  slotInterval: number
  slotBuffer?: number
  appoinmentPerSlot?: number
  appoinmentPerDay?: number
  allowBookingAfter?: number
  allowBookingAfterUnit?: AllowBookingAfter
  allowBookingFor?: number
  allowBookingForUnit?: AllowBookingFor
  enableOfficeHours?: boolean
  openHours?: IOpenHour
  enableRecurring: boolean
  recurring?: IRecurring
  formId?: string
  stickyContact?: boolean
  stripe?: IStripe
  isLivePaymentMode?: boolean
  notifications?: ICalendarNotification[]
  autoConfirm?: boolean
  shouldSendAlertEmailsToAssignedMember?: boolean
  enableStaffSelection?: boolean
  alertEmail?: string
  googleInvitationEmails?: boolean
  allowReschedule?: boolean
  allowCancellation?: boolean
  shouldAssignContactToTeamMember?: boolean
  shouldSkipAssigningContactForExisting?: boolean
  notes?: string
  pixelId?: string
  codeBlock?: string
  formSubmitType?: FormSubmitType
  formSubmitRedirectURL?: string
  formSubmitThanksMessage?: string
  isActive?: boolean
  deleted?: boolean
  availabilities:Availability[]
  availabilityType?:AvailabilityType
  enableGuests?:boolean
}

export interface IOpenHour {
  daysOfTheWeek: DaysOfWeek[]
  hours: IHour[]
}

export enum CalendarType {
  ROUND_ROBIN = 'round_robin',
  EVENT = 'event',
  CLASS_BOOKING = 'class_booking'
}

export enum WidgetType {
  DEFAULT = 'default',
  CLASSIC = 'classic'
}
export enum LogoShape {
  SQUARE = 'square',
  ROUND = 'round'
}
export interface WidgetLogo {
  url?: string
  shape?: LogoShape
}


export interface IHour {
  openHour: number
  openMinute: number
  closeHour: number
  closeMinute: number
}

export interface ILinkedCalendars {
  clio?: IClioCalendar
  drchrono?: IDrChrono
  google?: IGoogleCalendar
}

export interface IClioCalendar {
  id: string
  name: string
}

export interface IDrChrono {
  id: string
  name: string
  syncAllDoctors?: boolean
  doctorId: string
  lastSync?: string
}

export interface IGoogleCalendar {
  id: string
  name: string
  accessRole: string
  watchId: string
  expiration: number
  userId: string
  oauthId?: string
  lastSync?: string
  error?: number
  errorMessage?: string
}

export interface IHomeServiceData {
  categoryType: string
  jobType: string
}

export interface IHomeServicePrice {
  currencyCode: string
  priceMicros: number
}

export interface IStripe {
  amount: number
  currency: number
  chargeDescription?: string
}

export interface ITeamMember {
  userId: string
  priority: number
  meetingLocation: string
  selected: boolean
  isZoomAdded: boolean | string
  isGmeetAllowed: boolean
  zoomOauthId: string
}

enum NotificationType {
  EMAIL = 'email',
  SMS = 'sms',
}

export enum AvailabilityType {
  STANDARD = 0,
  CUSTOM = 1
}

export interface Availability {
  _id: string
  calendarId: string
  date: string
  hours: IHour[]
  deleted:boolean
}

export interface ICalendarNotification {
  type: NotificationType
  shouldSendToContact: boolean
  shouldSendToGuest: boolean
  shouldSendToUser: boolean
  shouldSendToSelectedUsers: boolean
  selectedUsers: string
  templateId: string
}

export interface IRRule {
  freq: RecurringFrequency
  count: number
  interval?: number
  weekDays?: WeekDay[]
  monthDays?: number[]
}

export interface IRecurring extends IRRule {
  bookingOption: RecurringBookingOption
  bookingOverlapDefaultStatus?: RecurringOverlapStatusOption
}

export enum RecurringFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum RecurringBookingOption {
  SKIP = 'skip',
  CONTINUE = 'continue',
  BOOK_NEXT = 'book_next',
}

export enum RecurringOverlapStatusOption {
  CONFIRMED = 'confirmed',
  UNCONFIRMED = 'unconfirmed',
}

export enum WeekDay {
  SUNDAY = 'SU',
  MONDAY = 'MO',
  TUESDAY = 'TU',
  WEDNESDAY = 'WE',
  THURSDAY = 'TH',
  FRIDAY = 'FR',
  SATURDAY = 'SA',
}

export enum SyncOption {
  TWOWAY = 'twoway',
  ONEWAY = 'oneway',
  DISABLE_TRIGGER = 'disable_trigger',
  SMART = 'smart',
}

export enum EventType {
  RoundRobin_OptimizeForAvailability = 'RoundRobin_OptimizeForAvailability',
  RoundRobin_OptimizeForEqualDistribution = 'RoundRobin_OptimizeForEqualDistribution',
  Collective = 'Collective',
  Group = 'Group',
}

export enum FormSubmitType {
  RedirectURL = 'RedirectURL',
  ThankYouMessage = 'ThankYouMessage',
}

export enum ActiveStepType {
  Step1 = 1,
  Step2 = 2,
  Step3 = 3,
  Complete = 'Complete',
}

export type AllowBookingAfter = 'hours' | 'days' | 'weeks' | 'months'

export type AllowBookingFor = 'days' | 'weeks' | 'months'

export type  DaysOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6

const indexwiseWeekdays = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday'
}

export default class Calendar extends BaseModel {
  public static NotesText_Title_RescheduleOrCancellation =
    'Need to make a change to this event?\n'
  public static NotesText_Reschedule = 'Reschedule:-\n{{reschedule_link}}\n\n'
  public static NotesText_Cancellation = 'Cancel:-\n{{cancellation_link}}'
  static requestBodyWhiteList: string[] = []

  constructor(data: ICalendar) {
    super(Calendar.requestBodyWhiteList, data)

    this.data.eventType = this.eventType
    this.data.syncOption = this.syncOption
    this.data.eventTitle = this.eventTitle
    this.data.eventColor = this.eventColor
    this.data.enableStaffSelection = this.enableStaffSelection
    this.data.teamMembers = this.teamMembers
    this.data.linkedCalendars = this.linkedCalendars
    this.data.slotDuration = this.slotDuration
    this.data.slotInterval = this.slotInterval
    this.data.allowBookingAfterUnit = this.allowBookingAfterUnit
    this.data.allowBookingForUnit = this.allowBookingForUnit
    this.data.enableOfficeHours = this.enableOfficeHours
    this.data.openHours = this.openHours
    this.data.enableRecurring = this.enableRecurring
    this.data.recurring = this.recurring
    this.data.autoConfirm = this.autoConfirm
    this.data.googleInvitationEmails = this.googleInvitationEmails
    this.data.allowReschedule = this.allowReschedule
    this.data.allowCancellation = this.allowCancellation
    this.data.notes = this.notes
    this.data.stripe = this.stripe
    this.data.pixelId = this.pixelId
    this.data.isLivePaymentMode = this.isLivePaymentMode
    this.data.formSubmitType = this.formSubmitType
    this.data.formSubmitThanksMessage = this.formSubmitThanksMessage
    this.data.formSubmitRedirectURL = this.formSubmitRedirectURL
    this.data.reserveService = this.reserveService
    this.data.homeServiceData = this.homeServiceData
    this.data.price = this.price
    this.data.notifications = this.notifications
    this.data.shouldSendAlertEmailsToAssignedMember = this.shouldSendAlertEmailsToAssignedMember
    this.data.widgetType = this.widgetType
    this.data.enableGuests = this.enableGuests

    if (this.data.teamMembers.length > 1) {
      this.data.enableRecurring = false
    }

    if (this.data.widgetType !== WidgetType.DEFAULT) {
      this.data.enableStaffSelection = false
    }

    if (
      Array.isArray(this.data.notifications) &&
      !this.data.notifications.length
    ) {
      this.data.notifications.push({
        type: 'email',
        shouldSendToContact: false,
        shouldSendToGuest: false,
        shouldSendToUser: false,
        shouldSendToSelectedUsers: false,
        selectedUsers: '',
        templateId: 'default',
      })
    }
  }

  async save(): Promise<{ [key: string]: any }> {
    return new Promise(async (resolve, reject) => {
      try {
        let response: { [key: string]: any }
        if (this.newRecord) {
          const selectedData =
            Calendar.requestBodyWhiteList.length === 0
              ? this.data
              : Calendar.requestBodyWhiteList.reduce((acc, cv) => {
                  acc[cv] = this.data[cv]
                  return acc
                }, {})
          if (selectedData.teamMembers?.length === 0) {
            delete selectedData.teamMembers
          }

          response = await CalendarService.create(
            selectedData as ICalendar
          )
          this.data.id = response.calendar.id
        } else {

          if (this.updatedData.teamMembers?.length === 0) {
            delete this.updatedData.teamMembers
          }

          response = await CalendarService.update(
            this.id,
            this.updatedData as Partial<ICalendar>
          )
        }
        this.clearUpdatedData()
        this.newRecord = false
        if(response.calendar.availabilities){
          this.data.availabilities = response.calendar.availabilities
        }
        resolve(new Calendar(response.calendar))
      } catch (error) {
        reject(error)
      }
    })
  }

  static async getById(id: string): Promise<Calendar> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await CalendarService.read(id)
        const transformerMap = new Map<String, any>()
        transformerMap.set('formSubmitRedirectUrl', 'formSubmitRedirectURL')
        for (const [key, value] of Object.entries(response.calendar)) {
          const newKey = transformerMap.get(key)
          if (newKey) {
            delete response.calendar[key]
            response.calendar[newKey] = value
          }
        }
        resolve(new Calendar(response.calendar))
      } catch (error) {
        reject(error)
      }
    })
  }

  static async isValidCalendarSlug(
    groupId: string,
    slug: string,
    calendarId?: string
  ): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await CalendarService.isValidCalendarSlug({
          groupId: groupId || "",
          slug,
          calendarId,
        })
        resolve(response)
      } catch (error) {
        reject(error)
      }
    })
  }

  static async generateRRuleText(rrule: IRRule): Promise<String> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await CalendarService.generateRRuleText(rrule)
        resolve(response)
      } catch (error) {
        reject(error)
      }
    })
  }

  async deleteLinkedGoogleCalendarV2() {
    if (!this.linkedCalendars.google?.id) {
      return
    }
    if (has(this.linkedCalendars, 'google.id')) {
      try {
        await axios.get(`/google/calendar/unlink?calendar_id=${this.id}`)
      } catch (err) {}
    }
  }

  get id(): string {
    return this.data.id
  }

  set id(id: string) {
    this.data.id = id
  }

  get locationId(): string {
    return this.data.locationId
  }

  set locationId(locationId: string) {
    this.data.locationId = locationId
    this.updatedData.locationId = locationId
  }

  get groupId(): string {
    return this.data.groupId
  }

  set groupId(groupId: string) {
    this.data.groupId = groupId
    this.updatedData.groupId = groupId
  }

  get teamMembers(): ITeamMember[] {
    return this.data.teamMembers || []
  }

  set teamMembers(teamMembers: ITeamMember[]) {
    this.data.teamMembers = teamMembers
    this.updatedData.teamMembers = teamMembers
  }

  get enableStaffSelection(): boolean {
    return this.data.enableStaffSelection ?? false
  }

  set enableStaffSelection(enableStaffSelection: boolean) {
    this.data.enableStaffSelection = enableStaffSelection
    this.updatedData.enableStaffSelection = enableStaffSelection
  }

  get eventType(): EventType {
    return this.data.eventType || EventType.RoundRobin_OptimizeForAvailability
  }

  set eventType(eventType: EventType) {
    this.data.eventType = eventType
    this.updatedData.eventType = eventType
  }

  get addServiceActiveStep(): ActiveStepType {
    return this.data.addServiceActiveStep || ActiveStepType.Step1
  }

  set addServiceActiveStep(addServiceActiveStep: ActiveStepType) {
    this.data.addServiceActiveStep = addServiceActiveStep
    this.updatedData.addServiceActiveStep = addServiceActiveStep
  }

  get name(): string {
    return this.data.name
  }

  set name(name: string) {
    this.data.name = name
    this.updatedData.name = name
  }

  get description(): string {
    return this.data.description
  }

  set description(description: string) {
    this.data.description = description
    this.updatedData.description = description
  }

  get slug(): string {
    return this.data.slug
  }

  set slug(slug: string) {
    this.data.slug = slug
    this.updatedData.slug = slug
  }

  get eventTitle(): string {
    return this.data.eventTitle !== undefined
      ? this.data.eventTitle
      : '{{contact.name}}'
  }

  set eventTitle(eventTitle: string) {
    this.data.eventTitle = eventTitle
    this.updatedData.eventTitle = eventTitle
  }

  get eventColor(): string {
    return this.data.eventColor || '#039BE5'
  }

  set eventColor(eventColor: string) {
    this.data.eventColor = eventColor
    this.updatedData.eventColor = eventColor
  }

  get meetingLocation(): string {
    return this.data.meetingLocation
  }

  set meetingLocation(meetingLocation: string) {
    this.data.meetingLocation = meetingLocation
    this.updatedData.meetingLocation = meetingLocation
  }

  get linkedCalendars(): ILinkedCalendars {
    if (!this.data.linkedCalendars) {
      this.data.linkedCalendars = {}
    }
    if (!this.data.linkedCalendars.clio) {
      this.data.linkedCalendars.clio = {}
    }
    if (!this.data.linkedCalendars.drchrono) {
      this.data.linkedCalendars.drchrono = {}
    }
    if (!this.data.linkedCalendars.google) {
      this.data.linkedCalendars.google = {}
    }
    return this.data.linkedCalendars
  }

  set linkedCalendars(linkedCalendars: ILinkedCalendars) {
    this.data.linkedCalendars = linkedCalendars
    this.updatedData.linkedCalendars = linkedCalendars
  }

  get syncOption(): SyncOption {
    return this.data.syncOption || SyncOption.ONEWAY
  }

  set syncOption(syncOption: SyncOption) {
    this.data.syncOption = syncOption
    this.updatedData.syncOption = syncOption
  }

  get reserveService(): boolean {
    return this.data.reserveService ?? false
  }

  set reserveService(reserveService: boolean) {
    this.data.reserveService = reserveService
    this.updatedData.reserveService = reserveService
  }

  get homeServiceData(): IHomeServiceData {
    return this.data.homeServiceData
  }

  set homeServiceData(homeServiceData: IHomeServiceData) {
    this.data.homeServiceData = homeServiceData
    this.updatedData.homeServiceData = homeServiceData
  }

  get price(): IHomeServicePrice {
    return this.data.price
  }

  set price(price: IHomeServicePrice) {
    this.data.price = price
    this.updatedData.price = price
  }

  get slotDuration(): number {
    return this.data.slotDuration || 30
  }

  set slotDuration(slotDuration: number) {
    this.data.slotDuration = slotDuration
    this.updatedData.slotDuration = slotDuration
  }

  get slotDurationUnit(): string {
    return this.data.slotDurationUnit || "mins"
  }

  set slotDurationUnit(slotDurationUnit: string) {
    this.data.slotDurationUnit = slotDurationUnit
    this.updatedData.slotDurationUnit = slotDurationUnit
  }

  get slotInterval(): number {
    return this.data.slotInterval || 30
  }

  set slotInterval(slotInterval: number) {
    this.data.slotInterval = slotInterval
    this.updatedData.slotInterval = slotInterval
  }

  get slotBuffer(): number {
    return this.data.slotBuffer
  }

  set slotBuffer(slotBuffer: number) {
    this.data.slotBuffer = slotBuffer
    this.updatedData.slotBuffer = slotBuffer
  }

  get appoinmentPerSlot(): number {
    return this.data.appoinmentPerSlot
  }

  set appoinmentPerSlot(appoinmentPerSlot: number) {
    this.data.appoinmentPerSlot = appoinmentPerSlot
    this.updatedData.appoinmentPerSlot = appoinmentPerSlot
  }

  get appoinmentPerDay(): number {
    return this.data.appoinmentPerDay
  }

  set appoinmentPerDay(appoinmentPerDay: number) {
    this.data.appoinmentPerDay = appoinmentPerDay
    this.updatedData.appoinmentPerDay = appoinmentPerDay
  }

  get allowBookingAfter(): number {
    return this.data.allowBookingAfter
  }

  set allowBookingAfter(allowBookingAfter: number) {
    this.data.allowBookingAfter = allowBookingAfter
    this.updatedData.allowBookingAfter = allowBookingAfter
  }

  get allowBookingAfterUnit(): AllowBookingAfter {
    return this.data.allowBookingAfterUnit || 'days'
  }

  set allowBookingAfterUnit(allowBookingAfterUnit: AllowBookingAfter) {
    this.data.allowBookingAfterUnit = allowBookingAfterUnit
    this.updatedData.allowBookingAfterUnit = allowBookingAfterUnit
  }

  get allowBookingFor(): number {
    return this.data.allowBookingFor
  }

  set allowBookingFor(allowBookingFor: number) {
    this.data.allowBookingFor = allowBookingFor
    this.updatedData.allowBookingFor = allowBookingFor
  }

  get allowBookingForUnit(): AllowBookingFor {
    return this.data.allowBookingForUnit || 'days'
  }

  set allowBookingForUnit(allowBookingForUnit: AllowBookingFor) {
    this.data.allowBookingForUnit = allowBookingForUnit
    this.updatedData.allowBookingForUnit = allowBookingForUnit
  }

  get enableOfficeHours(): boolean {
    return true
  }

  set enableOfficeHours(enableOfficeHours: boolean) {
    this.data.enableOfficeHours = enableOfficeHours
    this.updatedData.enableOfficeHours = enableOfficeHours
  }

  get openHours(): IOpenHour {
    const defaultOpenHours: IOpenHour[] = range(1, 6).map((day: number) => ({
      daysOfTheWeek: [day] as DaysOfWeek[],
      hours: [
        {
          openHour: 8 as number,
          openMinute: 0 as number,
          closeHour: 17 as number,
          closeMinute: 0 as number,
        },
      ] as IHour[],
    }))
    return this.data.openHours || defaultOpenHours
  }

  set openHours(openHours: IOpenHour) {
    this.data.openHours = openHours
    this.updatedData.openHours = openHours
  }

  get enableRecurring(): boolean {
    return this.data.enableRecurring ?? false
  }

  set enableRecurring(enableRecurring: boolean) {
    this.data.enableRecurring = enableRecurring
    this.updatedData.enableRecurring = enableRecurring
  }

  get recurring(): IRecurring {
    const defaultRecurring: IRecurring = {
      freq: RecurringFrequency.DAILY,
      count: 1,
      interval: undefined,
      weekDays: [],
      monthDays: [],
      bookingOption: RecurringBookingOption.SKIP,
      bookingOverlapDefaultStatus: RecurringOverlapStatusOption.CONFIRMED,
    }
    if (
      typeof this.data.recurring === 'object' &&
      Object.keys(this.data.recurring).length
    ) {
      return this.data.recurring
    }
    return defaultRecurring
  }

  set recurring(recurring: IRecurring) {
    this.data.recurring = recurring
    this.updatedData.recurring = recurring
  }

  get isCustomRecurrence(): boolean {
    return !!this.data.recurring?.interval
  }

  get formId(): string {
    return this.data.formId
  }

  set formId(formId: string) {
    this.data.formId = formId
    this.updatedData.formId = formId
  }

  get stickyContact(): boolean {
    return this.data.stickyContact ?? false
  }

  set stickyContact(stickyContact: boolean) {
    this.data.stickyContact = stickyContact
    this.updatedData.stickyContact = stickyContact
  }

  get stripe(): IStripe {
    return this.data.stripe || <IStripe>{}
  }

  set stripe(stripe: IStripe) {
    this.data.stripe = stripe
    this.updatedData.stripe = stripe
  }

  get isLivePaymentMode(): Boolean {
    return this.data.isLivePaymentMode || false
  }

  set isLivePaymentMode(isLivePaymentMode: Boolean) {
    this.data.isLivePaymentMode = isLivePaymentMode
    this.updatedData.isLivePaymentMode = isLivePaymentMode
  }

  get notifications(): ICalendarNotification[] {
    return this.data.notifications || []
  }

  set notifications(notifications: ICalendarNotification[]) {
    this.data.notifications = notifications
    this.updatedData.notifications = notifications
  }

  get autoConfirm(): boolean {
    return this.data.autoConfirm ?? true
  }

  set autoConfirm(autoConfirm: boolean) {
    this.data.autoConfirm = autoConfirm
    this.updatedData.autoConfirm = autoConfirm
  }

  get isEventCalendar() {
    return this.calendarType === CalendarType.EVENT
  }

  get isRoundRobinCalendar() {
    return this.calendarType === CalendarType.ROUND_ROBIN
  }

  get isClassBookingCalendar() {
    return this.calendarType === CalendarType.CLASS_BOOKING
  }

  get shouldSendAlertEmailsToAssignedMember(): boolean {
    return this.data.shouldSendAlertEmailsToAssignedMember ?? true
  }

  set shouldSendAlertEmailsToAssignedMember(
    shouldSendAlertEmailsToAssignedMember: boolean
  ) {
    this.data.shouldSendAlertEmailsToAssignedMember = shouldSendAlertEmailsToAssignedMember
    this.updatedData.shouldSendAlertEmailsToAssignedMember = shouldSendAlertEmailsToAssignedMember
  }

  get calendarType(): CalendarType {
    return this.data.calendarType
  }

  set calendarType(value: CalendarType) {
    this.data.calendarType = value
    this.updatedData.calendarType = value
  }

  get widgetType(): WidgetType {
    return this.data.widgetType || "default"
  }

  set widgetType(value: WidgetType) {
    this.data.widgetType = value
    this.updatedData.widgetType = value
  }
  get widgetLogo(): WidgetLogo {
    return this.data.widgetLogo || {url:'', shape: LogoShape.SQUARE}
  }

  set widgetLogo(value: WidgetLogo) {
    this.data.widgetLogo = value
    this.updatedData.widgetLogo = value
  }

  get widgetSlug(): string {
    return this.data.widgetSlug
  }

  set widgetSlug(value: string) {
    this.data.widgetSlug = value
    this.updatedData.widgetSlug = value
  }

  get alertEmail(): string {
    return this.data.alertEmail
  }

  set alertEmail(alertEmail: string) {
    this.data.alertEmail = alertEmail
    this.updatedData.alertEmail = alertEmail
  }

  get googleInvitationEmails(): boolean {
    return this.data.googleInvitationEmails ?? false
  }

  set googleInvitationEmails(googleInvitationEmails: boolean) {
    this.data.googleInvitationEmails = googleInvitationEmails
    this.updatedData.googleInvitationEmails = googleInvitationEmails
  }

  get allowReschedule(): boolean {
    return this.data.allowReschedule ?? true
  }

  set allowReschedule(allowReschedule: boolean) {
    this.data.allowReschedule = allowReschedule
    this.updatedData.allowReschedule = allowReschedule
  }

  get allowCancellation(): boolean {
    return this.data.allowCancellation ?? true
  }

  set allowCancellation(allowCancellation: boolean) {
    this.data.allowCancellation = allowCancellation
    this.updatedData.allowCancellation = allowCancellation
  }

  get shouldAssignContactToTeamMember(): boolean {
    return this.data.shouldAssignContactToTeamMember
  }

  set shouldAssignContactToTeamMember(
    shouldAssignContactToTeamMember: boolean
  ) {
    this.data.shouldAssignContactToTeamMember = shouldAssignContactToTeamMember
    this.updatedData.shouldAssignContactToTeamMember = shouldAssignContactToTeamMember
  }

  get shouldSkipAssigningContactForExisting(): boolean {
    return this.data.shouldSkipAssigningContactForExisting
  }

  set shouldSkipAssigningContactForExisting(
    shouldSkipAssigningContactForExisting: boolean
  ) {
    this.data.shouldSkipAssigningContactForExisting = shouldSkipAssigningContactForExisting
    this.updatedData.shouldSkipAssigningContactForExisting = shouldSkipAssigningContactForExisting
  }

  get notes(): string {
    return this.data.notes !== undefined
      ? this.data.notes
      : 'Phone:- {{contact.phone}}\nEmail:- {{contact.email}}\n\nNeed to make a change to this event?\nReschedule:-\n{{reschedule_link}}\n\nCancel:-\n{{cancellation_link}}'
  }

  set notes(notes: string) {
    this.data.notes = notes
    this.updatedData.notes = notes
  }

  get pixelId(): string {
    return this.data.pixelId
  }

  set pixelId(pixelId: string) {
    this.data.pixelId = pixelId
    this.updatedData.pixelId = pixelId
  }

  get codeBlock(): string {
    return this.data.codeBlock
  }

  set codeBlock(codeBlock: string) {
    this.data.codeBlock = codeBlock
    this.updatedData.codeBlock = codeBlock
  }

  get formSubmitType(): FormSubmitType {
    return this.data.formSubmitType || FormSubmitType.ThankYouMessage
  }

  set formSubmitType(formSubmitType: FormSubmitType) {
    this.data.formSubmitType = formSubmitType
    this.updatedData.formSubmitType = formSubmitType
  }

  get thankyouURL(): string {
    return this.data.thankYouUrl
  }

  set thankyouURL(thankyouURL: string) {
    this.data.thankYouUrl = thankyouURL
    this.updatedData.thankYouUrl = thankyouURL
  }

  get formSubmitRedirectURL(): string {
    return this.data.formSubmitRedirectURL || this.thankyouURL
  }

  set formSubmitRedirectURL(formSubmitRedirectURL: string) {
    this.data.formSubmitRedirectURL = formSubmitRedirectURL
    this.updatedData.formSubmitRedirectURL = formSubmitRedirectURL
  }

  get formSubmitThanksMessage(): string {
    return (
      this.data.formSubmitThanksMessage ||
      'Thank you for your appointment request. We will contact you shortly to confirm your request. Please call our office at {{contactMethod}} if you have any questions.'
    )
  }

  set formSubmitThanksMessage(formSubmitThanksMessage: string) {
    this.data.formSubmitThanksMessage = formSubmitThanksMessage
    this.updatedData.formSubmitThanksMessage = formSubmitThanksMessage
  }

  get isActive(): boolean {
    return this.data.isActive ?? true
  }

  set isActive(isActive: boolean) {
    this.data.isActive = isActive
    this.updatedData.isActive = isActive
  }

  get deleted(): boolean {
    return this.data.deleted ?? false
  }

  set deleted(deleted: boolean) {
    this.data.deleted = deleted
    this.updatedData.deleted = deleted
  }

  get availabilityType(): number {
    return this.data.availabilityType
      ? this.data.availabilityType
      : AvailabilityType.STANDARD
  }

  set availabilityType(type: number) {
    this.data.availabilityType = type
    this.updatedData.availabilityType = type
  }

  get availabilities(): Availability[] {
    return this.data.availabilities || []
  }

  get enableGuests(): boolean {
    return this.data.enableGuests
  }

  set enableGuests(value: boolean) {
    this.data.enableGuests = value
    this.updatedData.enableGuests = value
  }

  set availabilities(value: Availability[]) {
    const availabilitiesDate = value
      ? value
          .filter(v => !(v.deleted && v._id === undefined))
          .map(v => {
            const availability = {
              date: v.date,
              hours: v.hours,
              deleted: v.deleted,
            }
            if (v._id) {
              availability['id'] = v._id
            }
            return availability
          })
      : []
    this.data.availabilities = availabilitiesDate
    this.updatedData.availabilities = availabilitiesDate
  }

  get isStandardAvailability(): boolean {
    return this.availabilityType == AvailabilityType.STANDARD
  }

  get dayOfTheWeekwiseAvailability() {
    const _dayOfTheWeekwiseAvailability = {}
    if (this.data.openHours) {
      this.data.openHours.forEach(x => {
        _dayOfTheWeekwiseAvailability[indexwiseWeekdays[x.daysOfTheWeek]] =
          x.hours
      })
    }
    return _dayOfTheWeekwiseAvailability
  }

  repeatTheOpenHoursForOtherWeekDays(dayOfWeek: number) {
    const pickedDayOfWeekOpenHours = this.data.openHours.find(
      x => x.daysOfTheWeek[0] === dayOfWeek
    )
    this.openHours = this.data.openHours.map(x => {
      return {
        daysOfTheWeek: x.daysOfTheWeek,
        hours: cloneDeep(pickedDayOfWeekOpenHours.hours),
      }
    })
  }
}
